import type { paths } from './schema'

/**
 * The admin API's shapes, **derived from the contract** rather than written by hand.
 *
 * These were hand-written first, because the backend did not exist yet. When it landed, five
 * DTOs had drifted: `recentLogins[].ip` had been written as `ipAddress`, `.success` as
 * `wasSuccessful`, a session's `lastSeenAt` as `lastUsedAt`, and the parameter and moderation
 * shapes were wrong outright. Every one of those is invisible at runtime — the request
 * succeeds, the column renders blank forever.
 *
 * So nothing here is declared any more. `npm run api:types` regenerates `schema.d.ts` from the
 * backend's `contract/openapi.json`, and every type below is projected out of it. A backend
 * change is now a compile error here, which is the contract doing its job.
 *
 * Byte counts are `string`, not `number`: they are BigInt on the server and `JSON.parse` would
 * silently round anything past 2^53. Pass them to `formatBytes`, which takes either.
 */

/** The 200 body of `GET <P>`. */
type Get200<P extends keyof paths> = paths[P] extends {
  get: { responses: { 200: { content: { 'application/json': infer B } } } }
}
  ? B
  : never

/** The 200 body of `POST <P>`. */
type Post200<P extends keyof paths> = paths[P] extends {
  post: { responses: { 200: { content: { 'application/json': infer B } } } }
}
  ? B
  : never

/** One element of a list endpoint's `items`. */
type ItemOf<T> = T extends { items: readonly (infer I)[] } ? I : never

// ---------------------------------------------------------------- pagination

export interface PageInfo {
  readonly nextCursor?: string
  readonly hasNextPage: boolean
}

export interface Page<T> {
  readonly items: readonly T[]
  readonly pageInfo: PageInfo
}

/** A list that also reports how many rows exist, for a table header. */
export interface CountedPage<T> extends Page<T> {
  readonly total: number
}

// ---------------------------------------------------------------- identity

export type AdminIdentity = Get200<'/admin/auth/me'>
export type AdminRole = AdminIdentity['roles'][number]

// ---------------------------------------------------------------- analytics

export type OverviewDto = Get200<'/admin/analytics/overview'>
export type StorageDto = Get200<'/admin/analytics/storage'>
export type TopWorldDto = ItemOf<Get200<'/admin/analytics/top-worlds'>>
export type ModerationSummaryDto = Get200<'/admin/analytics/moderation'>

/* The platform reads. Derived from the contract rather than written out, so a backend change
   surfaces here as a type error instead of a screen that quietly shows the wrong shape. */
export type GrowthDto = Get200<'/admin/analytics/growth'>
export type PlatformRetentionDto = Get200<'/admin/analytics/retention'>
export type AcquisitionDto = Get200<'/admin/analytics/acquisition'>
export type PlatformUsersDto = Get200<'/admin/analytics/users'>
export type UserDrilldownDto = Get200<'/admin/analytics/users/{userId}'>
export type OperationsDto = Get200<'/admin/analytics/operations'>
export type AnalyticsExportDto = Get200<'/admin/analytics/exports/{exportId}'>
export type PlatformContentDto = Get200<'/admin/analytics/content'>

export const USER_DIMENSIONS = ['country', 'platform', 'deviceClass', 'locale'] as const
export type UserDimension = (typeof USER_DIMENSIONS)[number]
export type TimeseriesPoint = ItemOf<
  { items: Get200<'/admin/analytics/timeseries'>['points'] }
>

export const TIMESERIES_METRICS = [
  'signups',
  'logins',
  'visits',
  'worlds_created',
  'assets_uploaded',
  'messages',
  'moderation_actions',
] as const
export type TimeseriesMetric = (typeof TIMESERIES_METRICS)[number]

export const TIMESERIES_INTERVALS = ['hour', 'day', 'week'] as const
export type TimeseriesInterval = (typeof TIMESERIES_INTERVALS)[number]

// ---------------------------------------------------------------- users

export type AdminUserListItem = ItemOf<Get200<'/admin/users/'>>
export type AdminUserDetail = Get200<'/admin/users/{userId}'>

export type AdminSession = AdminUserDetail['sessions'][number]
export type AdminIdentityLink = AdminUserDetail['identities'][number]
export type LoginAttemptDto = AdminUserDetail['recentLogins'][number]
export type EffectiveParameter = AdminUserDetail['parameters'][number]

export const USER_STATUSES = ['all', 'active', 'disabled', 'deleted'] as const
export type UserStatus = (typeof USER_STATUSES)[number]

// ---------------------------------------------------------------- worlds

export type AdminWorldListItem = ItemOf<Get200<'/admin/worlds/'>>
export type AdminWorldDetail = Get200<'/admin/worlds/{slug}'>
export type WorldInstanceDto = ItemOf<Get200<'/admin/worlds/{slug}/instances'>>
export type RoomVisitDto = ItemOf<Get200<'/admin/worlds/{slug}/visits'>>

export const WORLD_STATUSES = ['DRAFT', 'PUBLISHED', 'ARCHIVED'] as const
export type WorldStatus = (typeof WORLD_STATUSES)[number]

export const WORLD_PRIVACIES = ['PUBLIC', 'PRIVATE', 'CODE', 'INVITE'] as const
export type WorldPrivacy = (typeof WORLD_PRIVACIES)[number]

// ---------------------------------------------------------------- assets

export type AdminAssetListItem = ItemOf<Get200<'/admin/assets/'>>
export type AdminAssetDetail = Get200<'/admin/assets/{assetId}'>
export type AssetJobDto = ItemOf<Get200<'/admin/assets/jobs'>>

/* The backend enum, exactly. `MODEL_3D` — not `MODEL` — and two 360 variants the first
   guess at this list did not have, so their labels fell through to the raw value. */
export const ASSET_TYPES = [
  'MODEL_3D',
  'IMAGE',
  'VIDEO',
  'DOCUMENT',
  'IMAGE_360',
  'VIDEO_360',
  'SKYBOX',
  'AUDIO',
] as const
export type AssetType = (typeof ASSET_TYPES)[number]

export const ASSET_VISIBILITIES = ['PRIVATE', 'PUBLIC', 'PAID'] as const
export type AssetVisibility = (typeof ASSET_VISIBILITIES)[number]

/**
 * Processing states, as the backend's enum actually spells them.
 *
 * There is no `PENDING` — the queued state is `QUEUED`. Filtering on `PENDING` would have
 * quietly matched nothing.
 */
export const JOB_STATES = ['QUEUED', 'RUNNING', 'FAILED', 'DONE'] as const
export type JobState = (typeof JOB_STATES)[number]

// ---------------------------------------------------------------- avatars

export type CatalogAvatarDto = ItemOf<Get200<'/admin/avatars'>>
export type AvatarCategoryDto = ItemOf<Get200<'/admin/avatar-categories'>>

// ---------------------------------------------------------------- taxonomy

export type WorldCategoryDto = ItemOf<Get200<'/admin/world-categories/'>>

// ---------------------------------------------------------------- groups

export type GroupDto = ItemOf<Get200<'/admin/groups'>>
export type GroupMemberDto = ItemOf<Get200<'/admin/groups/{groupId}/members'>>
export type PermissionCategoryDto = ItemOf<Get200<'/admin/permission-categories'>>
export type PermissionChoice = PermissionCategoryDto['permissions'][number]

/** What `POST /admin/groups` answers with — used to select the new group after creating it. */
export type CreatedGroup = Post200<'/admin/groups'>

// ---------------------------------------------------------------- flags

export type FeatureFlagDto = ItemOf<Get200<'/admin/flags/'>>
export type FeatureFlagOverrideDto = FeatureFlagDto['overrides'][number]

// ---------------------------------------------------------------- parameters

export type ParameterDefinitionDto = ItemOf<Get200<'/admin/parameters/definitions'>>

// ---------------------------------------------------------------- moderation

export type ModerationActionDto = ItemOf<Get200<'/admin/moderation/actions'>>
export type RestrictionDto = ItemOf<Get200<'/admin/moderation/restrictions'>>

/** The moderation entries on an account's page — a narrower shape than the platform log. */
export type UserModerationEntry = AdminUserDetail['moderationAgainst'][number]

// ---------------------------------------------------------------- system

export type SystemHealthDto = Get200<'/admin/system/health'>
export type SystemInfoDto = Get200<'/admin/system/info'>
export type HealthCheck = SystemHealthDto['database']

// ---------------------------------------------------------------- audit

export type AuditEntryDto = ItemOf<Get200<'/admin/audit/'>>

// ---------------------------------------------------------------- releases

/** One platform's boot gate: which build is current, where to get it, and where its content lives. */
export type AppReleaseDto = ItemOf<Get200<'/admin/app-releases'>>

/**
 * The body of `PUT /admin/app-releases/{platform}`.
 *
 * Projected rather than written out, so the semver pattern and the required trio stay whatever the
 * backend says they are.
 */
export type AppReleaseInput = paths['/admin/app-releases/{platform}'] extends {
  put: { requestBody: { content: { 'application/json': infer B } } }
}
  ? B
  : never

export type AppReleasePlatform = AppReleaseDto['platform']
export type AppStoreKind = AppReleaseDto['storeKind']

/** What `POST /admin/content/{platform}` answers with once the zip is unpacked. */
export type ContentUploadResult = Post200<'/admin/content/{platform}'>

/**
 * One stored Unity WebGL build. Exactly one row in the list has `isActive`.
 *
 * Projected off the list rather than off the upload, which answers 201 and would need a second
 * projection helper to say the same thing. `totalBytes` is a string for the reason at the top of
 * this file; hand it to `formatBytes`.
 */
export type WebglBuildDto = ItemOf<Get200<'/admin/webgl-builds'>>
export type WebglLiveStatusDto = Get200<'/admin/webgl-builds/live'>
